import { useState, useEffect } from 'react';
import { Select, Space, Typography, Spin, message } from 'antd';
import { BankOutlined, SwapOutlined } from '@ant-design/icons';
import { companySwitcherService, Company } from '../services/companySwitcherService';

const { Text } = Typography;

interface CompanySwitcherProps {
  compact?: boolean;  // For mobile view
}

const CompanySwitcher: React.FC<CompanySwitcherProps> = ({ compact = false }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companySwitcherService.getAccessibleCompanies();
      if (response.success) {
        setCompanies(response.data.companies || []);
        setCurrentCompanyId(response.data.activeCompanyId);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
      // Don't show error message - user might not have multi-company access
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (companyId: string) => {
    if (companyId === currentCompanyId) return;

    try {
      setSwitching(true);
      const response = await companySwitcherService.switchCompany(companyId);
      if (response.success) {
        setCurrentCompanyId(companyId);
        const companyName = response.data.company?.name || 'new company';
        message.success(`Switched to ${companyName}`);
        // Reload the page to refresh all data with new company context
        window.location.reload();
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to switch company');
    } finally {
      setSwitching(false);
    }
  };

  // Don't render if user has no multi-company access (0 or 1 company)
  if (loading) {
    return <Spin size="small" />;
  }

  if (companies.length <= 1) {
    return null;
  }

  const currentCompany = companies.find(c => c.id === currentCompanyId);

  if (compact) {
    return (
      <Select
        value={currentCompanyId || undefined}
        onChange={handleCompanyChange}
        loading={switching}
        style={{ minWidth: 120 }}
        size="small"
        suffixIcon={<SwapOutlined />}
        options={companies.map(company => ({
          value: company.id,
          label: company.code,
        }))}
      />
    );
  }

  return (
    <Space size="small" style={{ marginRight: 16 }}>
      <BankOutlined style={{ color: '#1890ff', fontSize: 16 }} />
      <Select
        value={currentCompanyId || undefined}
        onChange={handleCompanyChange}
        loading={switching}
        style={{ minWidth: 200 }}
        placeholder="Select Company"
        optionLabelProp="label"
        dropdownStyle={{ minWidth: 250 }}
      >
        {companies.map(company => (
          <Select.Option
            key={company.id}
            value={company.id}
            label={
              <Space>
                <Text strong>{company.code}</Text>
                <Text type="secondary">-</Text>
                <Text>{company.name}</Text>
              </Space>
            }
          >
            <div style={{ padding: '4px 0' }}>
              <div>
                <Text strong>{company.name}</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Code: {company.code}
                </Text>
              </div>
            </div>
          </Select.Option>
        ))}
      </Select>
    </Space>
  );
};

export default CompanySwitcher;
